import { useEffect } from "react";
import { ClaimFormProvider, useClaimForm } from "./form/ClaimFormProvider";
import { useClaimSubmission } from "./form/useClaimSubmission";
import { ClaimTypeStep } from "./ClaimTypeStep";
import { IncidentDateStep } from "./IncidentDateStep";
import { DescriptionStep } from "./DescriptionStep";
import { ThankYouStep } from "./ThankYouStep";
import type { ClaimType } from "./types";

interface ClaimFormProps {
  contactId: string;
  onSubmitSuccess?: () => void;
}

const ClaimFormContent = ({ contactId, onSubmitSuccess }: ClaimFormProps) => {
  const {
    step,
    selectedType,
    incidentDateTime,
    description,
    setStep,
    setSelectedType,
    setIncidentDateTime,
    setDescription,
  } = useClaimForm();

  const { isSubmitting, handleSubmit } = useClaimSubmission(contactId, onSubmitSuccess);

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleNext = () => {
    if (step < 4) {
      setStep(step + 1);
    }
  };

  const handleTypeSelect = (type: ClaimType) => {
    setSelectedType(type);
    handleNext();
  };

  const handleSubmitClaim = async () => {
    const success = await handleSubmit({
      claim_type: selectedType as ClaimType,
      incident_date: incidentDateTime,
      description
    });

    if (success) {
      handleNext();
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <ClaimTypeStep
            selectedType={selectedType}
            onTypeSelect={handleTypeSelect}
          />
        );
      case 2:
        return (
          <IncidentDateStep
            incidentDateTime={incidentDateTime}
            onDateChange={setIncidentDateTime}
            onBack={handleBack}
            onNext={handleNext}
          />
        );
      case 3:
        return (
          <DescriptionStep
            description={description}
            onDescriptionChange={setDescription}
            onBack={handleBack}
            onSubmit={handleSubmitClaim}
            isSubmitting={isSubmitting}
          />
        );
      case 4:
        return <ThankYouStep />;
      default:
        return null;
    }
  };

  return (
    <div className="space-y-6">
      {renderStep()}
    </div>
  );
};

export const ClaimFormContainer = (props: ClaimFormProps) => {
  return (
    <ClaimFormProvider>
      <ClaimFormContent {...props} />
    </ClaimFormProvider>
  );
};